import { ErrorMessage, Field, FieldArray } from 'formik';
import { Button, Card, Col, Form, FormControl, Row } from 'react-bootstrap';
import Editor from '../../../../../components/editor/Editor';
import { FormLabel, FormStepHeader } from '../../../../../components/forms';
import {
  FormSelect,
  FormSelectOptions
} from '../../../../../components/tables/filterForms/FormSelect';
import { useGetLessonPlansQuery } from '../../../../../services/endpoints/content/lessonPlan';
import { StepProps } from './AddEditTheme';

const lessonPlanDropdownQuery = {
    data: { entries: 10000, orderBy: { field: 'name', order: 'asc' } }
};

export const ThemeForm = ({ touched, errors, values, setFieldValue, readOnly }: StepProps) => {
    let lessonPlanOptions: FormSelectOptions[] = [];
    const {data: lessonPlans} = useGetLessonPlansQuery(lessonPlanDropdownQuery);

    if (lessonPlans)
        lessonPlans.results.map((lesson) =>
            lessonPlanOptions.push({value: lesson.id, label: lesson.name})
        );

    return (
        <Row>
            <Col xs={12}>
                <FormStepHeader title="Details" showRequiredLegend={!readOnly} />
            </Col>
            <Col
                xs={12}
                lg={6}
                className="pe-1"
            >
                <Form.Group
                    className="mb-lg-3 mb-1"
                    controlId="name"
                >
                    <FormLabel required>Name</FormLabel>
                    <Field
                        name="name"
                        type="text"
                        as={Form.Control}
                        isInvalid={touched.name && !!errors.name}
                        aria-required
                    />
                    <ErrorMessage name="name">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                    </ErrorMessage>
                </Form.Group>
            </Col>

            <Col
                xs={12}
                lg={6}
                className="pe-1"
            >
                <Form.Group
                    className="mb-lg-3 mb-1"
                    controlId="is_public"
                >
                    <FormLabel required>Is public</FormLabel>
                    <Form.Control
                        as="select"
                        name="user_type"
                        value={values?.is_public}
                        onChange={(e) => setFieldValue!('is_public', e.target.value)}
                        isInvalid={touched.is_public && !!errors.is_public}
                        aria-required
                    >
                        <option>Select</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                    </Form.Control>
                    <ErrorMessage name="is_public">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                    </ErrorMessage>
                </Form.Group>
            </Col>
            <Col
                xs={12}
                lg={6}
                className="pe-1"
            >
                <Form.Group
                    className="mb-lg-3 mb-1"
                    controlId="is_ase"
                >
                    <FormLabel>ASE Theme (auto-populate lesson plans)</FormLabel>
                    <Form.Control
                        as="select"
                        name="is_ase"
                        value={values?.is_ase}
                        onChange={(e) => setFieldValue!('is_ase', e.target.value)}
                        isInvalid={touched.is_ase && !!errors.is_ase}
                    >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                    </Form.Control>
                    <ErrorMessage name="is_ase">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                    </ErrorMessage>
                </Form.Group>
            </Col>
            <Col
                xs={12}
                lg={12}
                className="pe-1"
            >
                <Form.Group
                    className="mb-lg-3 mb-10"
                    controlId="details"
                >
                    <FormLabel required>Details</FormLabel>
                    <Field
                        name="details"
                        component={Editor}
                        className="form-control"
                        isInvalid={touched.details && !!errors.details}
                        aria-required
                    />
                    <ErrorMessage name="details">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                    </ErrorMessage>
                </Form.Group>
            </Col>

            <FieldArray
                name="lesson_plans"
                render={(arrayHelpers) => (
                    <Form.Group
                        className="mb-lg-3 mb-1"
                        controlId="lesson_plans"
                    >
                        <Form.Label className="h4">Lesson Plans</Form.Label>
                        <br/>
                        <div className="multi-content-scroll mt-2">
                            <Row>
                                {values?.lesson_plans && values?.lesson_plans.length > 0 ? (
                                    values.lesson_plans.map((lesson_plan, index) => {
                                        let meta = {
                                            touched: Array.isArray(touched.lesson_plans)
                                                ? touched.lesson_plans[index]
                                                : {},
                                            error: Array.isArray(errors.lesson_plans)
                                                ? (errors.lesson_plans[index] ?? {})
                                                : {}
                                        };
                                        return (
                                            <Col
                                                key={index}
                                                lg={3}
                                                className="mb-2"
                                            >
                                                <Card border="primary">
                                                    {typeof errors.lesson_plans === 'string' ? (
                                                        <div>{errors.lesson_plans}</div>
                                                    ) : null}
                                                    {/* TODO: Check how the lesson plans works on Theme section */}
                                                    <Card.Body>
                                                        <Col className="ms-1">
                                                            <Row>
                                                                <Col
                                                                    xs={12}
                                                                    className="pe-1"
                                                                >
                                                                    <Form.Group
                                                                        className="mb-lg-3 mb-1"
                                                                        controlId={`lesson_plans.${index}.id`}
                                                                    >
                                                                        <Field
                                                                            as={FormSelect}
                                                                            name={`lesson_plans.${index}.id`}
                                                                            label="Name"
                                                                            options={lessonPlanOptions}
                                                                        />
                                                                    </Form.Group>

                                                                    <Form.Group
                                                                        className="mb-lg-3 mb-1"
                                                                        controlId={`lesson_plans.${index}.featured_on`}
                                                                    >
                                                                        <FormLabel>Featured On</FormLabel>
                                                                        <Field
                                                                            name={`lesson_plans.${index}.featured_on`}
                                                                            type="date"
                                                                            as={Form.Control}
                                                                            isInvalid={
                                                                                meta.touched?.featured_on &&
                                                                                !!(typeof meta.error === 'string'
                                                                                    ? meta.error
                                                                                    : meta.error.featured_on)
                                                                            }
                                                                        />
                                                                        <ErrorMessage
                                                                            name={`lesson_plans.${index}.featured_on`}>
                                                                            {(msg) => (
                                                                                <FormControl.Feedback type="invalid">
                                                                                    {msg}
                                                                                </FormControl.Feedback>
                                                                            )}
                                                                        </ErrorMessage>
                                                                    </Form.Group>

                                                                    <Form.Group
                                                                        className="mb-lg-3 mb-1"
                                                                        controlId={`lesson_plans.${index}.purpose`}
                                                                    >
                                                                        <FormLabel>Purpose</FormLabel>
                                                                        <Field
                                                                            name={`lesson_plans.${index}.purpose`}
                                                                            as={Form.Select}
                                                                            placeholder="Select"
                                                                            isInvalid={
                                                                                meta.touched?.purpose &&
                                                                                !!(typeof meta.error === 'string'
                                                                                    ? meta.error
                                                                                    : meta.error.purpose)
                                                                            }
                                                                        >
                                                                            <option>Select</option>
                                                                            <option value={'any_time'}>Any Time</option>
                                                                            <option value={'first_class'}>First Class
                                                                            </option>
                                                                        </Field>
                                                                        <ErrorMessage name="payment_method">
                                                                            {(msg) => (
                                                                                <FormControl.Feedback type="invalid">
                                                                                    {msg}
                                                                                </FormControl.Feedback>
                                                                            )}
                                                                        </ErrorMessage>
                                                                    </Form.Group>

                                                                    <Button
                                                                        variant="primary"
                                                                        size="sm"
                                                                        className="btn-box-shadow text-uppercase border-0"
                                                                        style={{
                                                                            width: 84,
                                                                            background: '#B81F69',
                                                                            fontSize: '.75rem'
                                                                        }}
                                                                        onClick={() => arrayHelpers.remove(index)}
                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                </Col>
                                                            </Row>
                                                        </Col>
                                                        {/* <Col hidden={index === 0} xs={1}>
                        <CloseButton onClick={() => arrayHelpers.remove(index)} />
                      </Col> */}
                                                    </Card.Body>
                                                    {/* { index < values.lesson_plans.length - 1 ? <hr /> : <></>} */}
                                                </Card>
                                            </Col>
                                        );
                                    })
                                ) : (
                                    <></>
                                )}
                            </Row>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-link fw-bolder text-uppercase link-primary link-underline-opacity-0"
                            onClick={() =>
                                arrayHelpers.insert(values?.lesson_plans.length ?? 0, {
                                    lesson_plan_id: null,
                                    featured_on: '',
                                    purpose: ''
                                })
                            } // insert an empty string at a position
                        >
                            + Add new Theme lesson plan
                        </button>
                    </Form.Group>
                )}
            />
        </Row>
    );
};
